/**
 * @author      IBS Team
 * @date        Jan, 2025
 * @copyright   www.ibirdsservices.com
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  InputGroup,
  Button,
  Card,
  Badge,
  Row,
  Col,
  Spinner,
} from 'react-bootstrap';
import DataApi from '../../../api/DataApi';
import { FaSearch, FaTimes } from 'react-icons/fa';

const TemplateSearchDialog = ({
  show,
  onHide,
  onSelect,
  selectedWhatsAppSetting,
  selectedTemplateId = null,
}) => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const categories = ['ALL', 'AUTHENTICATION', 'UTILITY', 'MARKETING'];

  useEffect(() => {
    if (show && selectedWhatsAppSetting) {
      fetchTemplates();
    } else {
      // Reset state when dialog closes
      setSearchQuery('');
      setSelectedCategory('ALL');
      setTemplates([]);
      setFilteredTemplates([]);
    }
  }, [show, selectedWhatsAppSetting]);

  useEffect(() => {
    filterTemplates();
  }, [templates, searchQuery, selectedCategory]);

  const fetchTemplates = async () => {
    if (!selectedWhatsAppSetting) {
      console.warn('No WhatsApp setting selected');
      return;
    }

    setLoading(true);
    try {
      const dataApi = new DataApi('watconnect');
      const result = await dataApi.fetchById(
        `watconnect_template?business_number=${selectedWhatsAppSetting}`
      );

      if (result?.data?.data && Array.isArray(result.data.data)) {
        // Filter only APPROVED templates (excluding PENDING_DELETION and DELETED) and sort by createddate DESC (newest first)
        const validTemplates = result.data.data
          .filter(
            t =>
              t.status &&
              t.status.toUpperCase() === 'APPROVED' &&
              t.status.toUpperCase() !== 'PENDING_DELETION' &&
              t.status.toUpperCase() !== 'DELETED'
          )
          .sort((a, b) => {
            const dateA = a.createddate ? new Date(a.createddate) : new Date(0);
            const dateB = b.createddate ? new Date(b.createddate) : new Date(0);
            return dateB - dateA;
          });

        setTemplates(validTemplates);
      } else {
        setTemplates([]);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const filterTemplates = () => {
    let filtered = [...templates];

    // Filter by category
    if (selectedCategory !== 'ALL') {
      filtered = filtered.filter(
        template =>
          template.category &&
          template.category.toUpperCase() === selectedCategory.toUpperCase()
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        template =>
          (template.template_name &&
            template.template_name.toLowerCase().includes(query)) ||
          (template.templatename &&
            template.templatename.toLowerCase().includes(query)) ||
          (template.template_id &&
            template.template_id.toString().toLowerCase().includes(query))
      );
    }

    setFilteredTemplates(filtered);
  };

  const handleSelect = template => {
    const templateId = template.template_id || template.id;
    const templateName = template.template_name || template.templatename || '';

    if (onSelect) {
      onSelect({
        id: templateId,
        name: templateName,
        category: template.category,
        ...template,
      });
    }
    onHide();
  };

  const getCategoryBadgeVariant = category => {
    switch (category?.toUpperCase()) {
      case 'MARKETING':
        return 'danger';
      case 'UTILITY':
        return 'info';
      case 'AUTHENTICATION':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  return (
    <Modal show={show} onHide={onHide} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title>Search WhatsApp Template</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Category Filter */}
        <Row className='mb-3'>
          <Col>
            <Form.Group>
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={selectedCategory}
                onChange={e => setSelectedCategory(e.target.value)}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat === 'ALL' ? 'All Categories' : cat}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* Search Input */}
        <Row className='mb-3'>
          <Col>
            <Form.Group>
              <Form.Label>Search Template</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type='text'
                  placeholder='Search by template name or ID...'
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <Button variant='outline-secondary' onClick={clearSearch}>
                    <FaTimes />
                  </Button>
                )}
              </InputGroup>
            </Form.Group>
          </Col>
        </Row>

        {/* Loading Spinner */}
        {loading && (
          <div className='text-center py-4'>
            <Spinner animation='border' variant='primary' />
            <p className='mt-2 text-muted'>Loading templates...</p>
          </div>
        )}

        {/* Template List */}
        {!loading && (
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {filteredTemplates.length === 0 ? (
              <div className='text-center py-4'>
                <p className='text-muted'>
                  {searchQuery || selectedCategory !== 'ALL'
                    ? 'No templates found matching your criteria.'
                    : 'No templates available.'}
                </p>
              </div>
            ) : (
              <div>
                <p className='text-muted mb-2'>
                  {filteredTemplates.length} template(s) found
                </p>
                {filteredTemplates.map((template, index) => {
                  const templateId = template.template_id || template.id;
                  const templateName =
                    template.template_name ||
                    template.templatename ||
                    'Unnamed Template';
                  const isSelected =
                    selectedTemplateId && selectedTemplateId === templateId;

                  return (
                    <Card
                      key={templateId || index}
                      className={`mb-2 ${isSelected ? 'border-primary' : ''}`}
                      style={{
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onClick={() => handleSelect(template)}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = '';
                      }}
                    >
                      <Card.Body>
                        <div className='d-flex justify-content-between align-items-start'>
                          <div className='flex-grow-1'>
                            <div className='d-flex align-items-center mb-1'>
                              <h6 className='mb-0 me-2'>{templateName}</h6>
                              <Badge
                                bg={getCategoryBadgeVariant(template.category)}
                              >
                                {template.category || 'UNKNOWN'}
                              </Badge>
                            </div>
                            {templateId && (
                              <small className='text-muted'>
                                ID: {templateId}
                              </small>
                            )}
                            {template.status && (
                              <div>
                                <Badge
                                  bg={
                                    template.status === 'APPROVED'
                                      ? 'success'
                                      : 'secondary'
                                  }
                                  className='mt-1'
                                >
                                  {template.status}
                                </Badge>
                              </div>
                            )}
                          </div>
                          {isSelected && <Badge bg='primary'>Selected</Badge>}
                        </div>
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onHide}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TemplateSearchDialog;
